import { useSeo } from '../hooks/useSeo';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, Send, Loader2, PenTool, Droplets, Layers, Palette, Ruler, Save, FolderOpen, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import { CATEGORIES, getCategory, defaultConfig, normalizeConfig } from '../configurator/catalog';
import { computeLayout } from '../configurator/layout';
import { makeCtx } from '../configurator/parts';
import { ViewCanvas, VIEWS } from '../configurator/ViewCanvas';
import { ModulesPanel } from '../configurator/ModulesPanel';
import { MaterialsPanel, SpacePanel } from '../configurator/Panels';
import { ExportStage } from '../configurator/ExportStage';
import { buildPdf } from '../configurator/exportPdf';
import { SendDialog } from '../configurator/SendDialog';
import { SaveDialog } from '../configurator/SaveDialog';
import { ShareDialog } from '../configurator/ShareDialog';
import { useAuth, startGoogleLogin } from '../hooks/useAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PENDING = 'jenma-pending-save';

const STORE = 'jenma-configurator-v1';
const loadStore = () => { try { return JSON.parse(localStorage.getItem(STORE)) || { byCat: {} }; } catch { return { byCat: {} }; } };
const newRef = (cat) => `JC-${cat.slice(0, 3).toUpperCase()}-${Date.now().toString(36).slice(-5).toUpperCase()}`;

function CategoryRail({ active, onSelect }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 snap-x" data-testid="category-rail">
      {CATEGORIES.map((c, i) => (
        <button key={c.id} type="button" onClick={() => onSelect(c.id)} data-testid={`category-${c.id}`}
          style={{ animationDelay: `${i * 60}ms` }}
          className={`snap-start shrink-0 w-56 text-left p-4 border transition-colors duration-300 animate-in fade-in slide-in-from-bottom-2 fill-mode-both ${active === c.id ? 'border-[#a68966] bg-[#a68966]/10' : 'border-white/10 hover:border-white/30'}`}>
          <span className="text-[10px] tracking-[0.3em] text-gray-500">0{i + 1}</span>
          <span className={`block font-playfair text-lg mt-1 ${active === c.id ? 'text-[#e6d3b3]' : 'text-gray-200'}`}>{c.label}</span>
          <span className="block text-[11px] text-gray-500 mt-1 leading-snug">{c.tagline}</span>
        </button>
      ))}
    </div>
  );
}

const Tab = ({ on, onClick, children, id }) => (
  <button type="button" onClick={onClick} data-testid={id}
    className={`shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 text-[10px] sm:text-xs uppercase tracking-[0.15em] sm:tracking-[0.2em] transition-colors duration-200 ${on ? 'bg-[#a68966] text-[#1a1a1a]' : 'text-gray-400 hover:text-white'}`}>
    {children}
  </button>
);

function Toolbar({ mode, setMode, technical, setTechnical, walls, wall, setWall }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
      <div className="flex max-w-full overflow-x-auto border border-white/10 bg-[#0f0f0f]" data-testid="view-mode-tabs">
        {Object.entries(VIEWS).map(([k, v]) => <Tab key={k} on={mode === k} onClick={() => setMode(k)} id={`view-tab-${k}`}>{v.label}</Tab>)}
        <Tab on={mode === 'all'} onClick={() => setMode('all')} id="view-tab-all">3 vues</Tab>
      </div>
      {mode === 'elevation' && walls.length > 1 && (
        <div className="flex border border-white/10" data-testid="elevation-wall-tabs">
          {walls.map((w) => <Tab key={w} on={wall === w} onClick={() => setWall(w)} id={`elevation-wall-${w}`}>Mur {w}</Tab>)}
        </div>
      )}
      <div className="flex items-center gap-4">
        {mode !== 'perspective' && <button type="button" onClick={() => setTechnical(!technical)} data-testid="render-style-toggle"
          className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gray-400 hover:text-[#a68966] transition-colors">
          {technical ? <PenTool className="w-4 h-4" /> : <Droplets className="w-4 h-4" />}
          {technical ? 'Trait technique' : 'Illustration'}
        </button>}
      </div>
    </div>
  );
}

export default function ConfiguratorPage() {
  useSeo("Configurateur d'agencement en ligne", "Composez votre cuisine, dressing, salle de bain, bar ou bureau sur mesure et obtenez plan, élévation et perspective en temps réel.", '/configurateur');
  const [params, setParams] = useSearchParams();
  const [store, setStore] = useState(loadStore);
  const active = getCategory(params.get('cat') || store.active || 'dressing').id;
  const defaults = useRef({});
  const fallback = (id) => { if (!defaults.current[id]) defaults.current[id] = defaultConfig(id); return defaults.current[id]; };
  const cfg = store.byCat[active] || fallback(active);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState('perspective');
  const [panel, setPanel] = useState('modules');
  const [technical, setTechnical] = useState(false);
  const view = 0.35;
  const [wallSel, setWall] = useState('A');
  const [exporting, setExporting] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [busyPdf, setBusyPdf] = useState(false);
  const [refCode, setRefCode] = useState(() => newRef(active));
  const resolver = useRef(null);
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const shareParam = params.get('share');
  const loadedShare = useRef(null);
  const projectParam = params.get('project');
  const lastSaved = useRef(null);
  const activeProject = project && project.category === active ? project : null;

  useEffect(() => {
    if (!projectParam || !user || project?.id === projectParam || lastSaved.current === projectParam) return;
    fetch(`${API_URL}/api/me/projects/${projectParam}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((pr) => {
        setStore((s) => ({ ...s, byCat: { ...s.byCat, [pr.category]: normalizeConfig(pr.config) } }));
        setProject(pr);
        toast.success(`Projet « ${pr.name} » chargé`);
      })
      .catch(() => toast.error('Projet introuvable'));
  }, [projectParam, user, project]);

  useEffect(() => {
    if (!shareParam || loadedShare.current === shareParam) return;
    loadedShare.current = shareParam;
    fetch(`${API_URL}/api/share/${shareParam}`).then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((sh) => {
        setStore((s) => ({ ...s, byCat: { ...s.byCat, [sh.category]: normalizeConfig(sh.config) } }));
        setProject(null);
        setParams({ cat: sh.category }, { replace: true });
        toast.success('Projet partagé importé : créez votre version !');
      })
      .catch(() => toast.error('Lien de partage introuvable'));
  }, [shareParam, setParams]);

  useEffect(() => {
    if (user && localStorage.getItem(PENDING) === active) { localStorage.removeItem(PENDING); setSaveOpen(true); }
  }, [user, active]);

  const onSaveClick = () => {
    if (user) { setSaveOpen(true); return; }
    localStorage.setItem(PENDING, active);
    toast.info('Connectez-vous avec Google pour enregistrer votre projet');
    startGoogleLogin(`/configurateur?cat=${active}`);
  };
  const onSaved = (pr) => { lastSaved.current = pr.id; setProject(pr); setParams({ cat: pr.category, project: pr.id }, { replace: true }); };

  useEffect(() => { localStorage.setItem(STORE, JSON.stringify({ ...store, active })); }, [store, active]);
  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { setSel(null); setRefCode(newRef(active)); }, [active]);

  const update = useCallback((fn) => setStore((s) => ({ ...s, byCat: { ...s.byCat, [active]: fn(s.byCat[active] || fallback(active)) } })), [active]);
  const L = useMemo(() => computeLayout(cfg), [cfg]);
  const c = useMemo(() => makeCtx('cv', cfg.mats, cfg.led, technical, cfg.facade), [cfg.mats, cfg.led, technical, cfg.facade]);
  const wall = L.walls.includes(wallSel) ? wallSel : 'A';
  const pick = useCallback((uid) => {
    if (!uid) return;
    const it = L.all.find((x) => x.uid === uid);
    if (it) setWall(it.wall);
    setSel(uid); setPanel('modules');
  }, [L]);
  const offset = wall === 'A' ? 0 : wall === 'B' ? L.items.length : L.items.length + L.itemsB.length;

  const makePdf = () => new Promise((resolve, reject) => { resolver.current = { resolve, reject }; setExporting({ L, c: makeCtx('x', cfg.mats, cfg.led, technical, cfg.facade), cfg }); });
  const onExported = async (images, err) => {
    const job = exporting; setExporting(null);
    if (!images) { resolver.current.reject(err); return; }
    resolver.current.resolve(await buildPdf({ cfg: job.cfg, L: job.L, images, ref: refCode }));
  };
  const download = async () => {
    setBusyPdf(true);
    try { (await makePdf()).save(`JENMA-Concept-${L.cat.label.replace(/[^a-z]/gi, '')}-${refCode}.pdf`); toast.success('Votre dossier PDF est prêt'); }
    catch { toast.error('La génération du PDF a échoué'); }
    finally { setBusyPdf(false); }
  };

  const panelUi = (
    <>
      <div className="flex border-b border-white/10 lg:sticky lg:top-[160px] bg-[#151515] z-10">
              {[['modules', 'Composition', Layers], ['materials', 'Matériaux', Palette], ['space', 'Espace', Ruler]].map(([k, label, Icon]) => (
                <button key={k} type="button" onClick={() => setPanel(k)} data-testid={`panel-tab-${k}`}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 text-[11px] uppercase tracking-[0.2em] border-b-2 transition-colors ${panel === k ? 'border-[#a68966] text-[#e6d3b3]' : 'border-transparent text-gray-500 hover:text-gray-300'}`}>
                  <Icon className="w-4 h-4" /> {label}
                </button>
              ))}
            </div>
            <div className="p-5">
              {panel === 'modules' && <ModulesPanel cfg={cfg} cat={L.cat} update={update} sel={sel} setSel={setSel} wall={wall} setWall={setWall} walls={L.walls} offset={offset} />}
              {panel === 'materials' && <MaterialsPanel cfg={cfg} update={update} />}
              {panel === 'space' && <SpacePanel cfg={cfg} L={L} update={update} onReset={() => { update(() => defaultConfig(active)); setSel(null); }} />}
            </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#121212] text-gray-200 pt-28 lg:pt-44 pb-32 lg:pb-24" data-testid="configurator-page">
      <div className="container mx-auto px-6">
        <header className="mb-8 max-w-3xl">
          <p className="text-[11px] uppercase tracking-[0.4em] text-[#a68966] mb-4 flex items-center gap-3"><span className="w-10 h-px bg-[#a68966]" />Atelier de conception</p>
          <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">Configurez votre <em className="text-[#d9c3a0]">agencement</em></h1>
          <p className="text-gray-400 mt-4 text-sm sm:text-base">Composez, ajustez et habillez chaque élément. Plan, élévation et vue 3D réaliste se construisent en temps réel, comme sur la table de nos architectes.</p>
        </header>
        <CategoryRail active={active} onSelect={(id) => setParams({ cat: id })} />

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-6 mt-8 items-start">
          <div className="contents lg:block min-w-0 lg:sticky lg:top-[168px]">
          <div className="sticky top-[84px] lg:static z-20 bg-[#121212] pb-2 lg:pb-0">
            <Toolbar mode={mode} setMode={setMode} technical={technical} setTechnical={setTechnical} walls={L.walls} wall={wall} setWall={setWall} />
            {mode === 'all' ? (
              <div className="grid md:grid-cols-2 gap-3">
                {Object.keys(VIEWS).map((k) => (
                  <div key={k} className={`bg-[#f6f1e8] ${k === 'perspective' ? 'md:col-span-2 h-[46vh]' : 'h-[34vh]'}`}>
                    <ViewCanvas kind={k} L={L} c={c} sel={sel} onPick={pick} technical={technical} view={view} p={`a${k}`} wall={wall} panel={panelUi} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#f6f1e8] h-[40vh] sm:h-[56vh] lg:h-[calc(100vh-300px)] lg:min-h-[420px] shadow-2xl animate-in fade-in duration-500" key={mode}>
                <ViewCanvas kind={mode} L={L} c={c} sel={sel} onPick={pick} technical={technical} view={view} p={`v${mode}`} wall={wall} panel={panelUi} />
              </div>
            )}
          </div>
            <div className="fixed lg:static bottom-0 inset-x-0 z-30 grid grid-cols-4 lg:flex lg:flex-wrap items-center gap-2 lg:gap-3 p-3 lg:p-0 lg:mt-3 bg-[#121212]/95 lg:bg-transparent backdrop-blur-xl lg:backdrop-blur-none border-t border-[#a68966]/20 lg:border-0" data-testid="configurator-action-bar">
              <button type="button" onClick={download} disabled={busyPdf} data-testid="download-pdf-btn"
                className="flex items-center justify-center gap-2 border border-[#a68966] text-[#e6d3b3] hover:bg-[#a68966] hover:text-[#1a1a1a] px-1.5 lg:px-5 py-3 text-[10px] lg:text-xs uppercase tracking-[0.08em] lg:tracking-[0.2em] transition-colors disabled:opacity-50">
                {busyPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 shrink-0" />} <span className="lg:hidden">PDF</span><span className="hidden lg:inline">Dossier PDF</span>
              </button>
              <button type="button" onClick={() => setSendOpen(true)} data-testid="open-send-dialog-btn"
                className="flex items-center justify-center gap-2 bg-[#a68966] hover:bg-[#b89c78] text-[#1a1a1a] font-semibold px-1.5 lg:px-5 py-3 text-[10px] lg:text-xs uppercase tracking-[0.08em] lg:tracking-[0.2em] transition-colors">
                <Send className="w-4 h-4 shrink-0" /> <span className="lg:hidden">Envoyer</span><span className="hidden lg:inline">Envoyer à JENMA</span>
              </button>
              <button type="button" onClick={onSaveClick} data-testid="save-project-btn"
                className="flex items-center justify-center gap-2 border border-white/15 text-gray-300 hover:border-[#4dffb8]/70 hover:text-[#4dffb8] px-1.5 lg:px-5 py-3 text-[10px] lg:text-xs uppercase tracking-[0.08em] lg:tracking-[0.2em] transition-colors">
                <Save className="w-4 h-4 shrink-0" /> <span className="lg:hidden">{activeProject ? 'MàJ' : 'Garder'}</span><span className="hidden lg:inline">{activeProject ? 'Mettre à jour' : 'Enregistrer'}</span>
              </button>
              <button type="button" onClick={() => setShareOpen(true)} data-testid="share-project-btn"
                className="flex items-center justify-center gap-2 border border-white/15 text-gray-300 hover:border-[#a68966] hover:text-[#e6d3b3] px-1.5 lg:px-5 py-3 text-[10px] lg:text-xs uppercase tracking-[0.08em] lg:tracking-[0.2em] transition-colors">
                <Share2 className="w-4 h-4 shrink-0" /> Partager
              </button>
              {activeProject && (
                <a href="/mon-espace" data-testid="current-project-chip" className="hidden lg:flex items-center gap-2 text-xs text-[#d9c3a0] hover:text-white transition-colors">
                  <FolderOpen className="w-4 h-4" /> {activeProject.name}
                </a>
              )}
              <span className="text-[11px] text-gray-500 ml-auto hidden xl:block">Touchez un élément, en 3D comme sur le plan, pour l'éditer</span>
            </div>
          </div>

          <aside className="min-w-0 bg-[#151515] border border-white/10 lg:min-h-[calc(100vh-200px)]" data-testid="configurator-panel">
            {panelUi}
          </aside>
        </div>
      </div>
      {exporting && <ExportStage L={exporting.L} c={exporting.c} technical={technical} onDone={onExported} />}
      <ShareDialog open={shareOpen} onOpenChange={setShareOpen} cfg={cfg} label={L.cat.label} name={activeProject?.name} />
      <SaveDialog open={saveOpen} onOpenChange={setSaveOpen} cfg={cfg} label={L.cat.label} project={activeProject} onSaved={onSaved} />
      <SendDialog user={user} open={sendOpen} onOpenChange={setSendOpen} cfg={cfg} L={L} makePdf={makePdf} refCode={refCode} />
    </div>
  );
}
